import React, { FunctionComponent } from 'react';
import classNames from 'classnames';
import Alert from '@reach/alert';

import Info from '@clearkit/icons/glyphs/Info';
import CheckmarkOrb from '@clearkit/icons/glyphs/CheckmarkOrb';
import Warning from '@clearkit/icons/glyphs/Warning';
import Error from '@clearkit/icons/glyphs/Error';
import Close from '@clearkit/icons/glyphs/Close';

import { Enum } from '../../common/types';
import { CKIconStack } from '../CKIconStack';

export const CKToastVariant = Enum(
  'default',
  'success',
  'warning',
  'error',
  'special',
  'info'
);

export type CKToastVariant = Enum<typeof CKToastVariant>;

export interface CKToastProps {
  className?: string;
  variant?: CKToastVariant;
  type?: 'assertive' | 'polite';
  icon?: React.ReactNode;
  heading: string | React.ReactNode;
  description?: string | React.ReactNode;
  onClose: () => void;
}

const getIconByVariant = (variant: CKToastVariant) => {
  switch (variant) {
    case 'default':
      return <Info />;
    case 'success':
      return <CheckmarkOrb />;
    case 'warning':
      return <Warning />;
    case 'error':
      return <Error />;
    case 'special':
      return null;
    case 'info':
      return <Info />;
  }
};

export const CKToast: FunctionComponent<CKToastProps> = ({
  className,
  variant = CKToastVariant.default,
  type = 'assertive',
  heading,
  description,
  icon,
  onClose,
}) => {
  const toastClass = classNames(`ck-toast`, `ck-toast--${variant}`, className);
  const variantIcon = getIconByVariant(variant);
  const toastIcon = icon ? (
    <CKIconStack base={icon} badge={variantIcon} />
  ) : (
    variantIcon
  );

  return (
    <Alert type={type} className={toastClass}>
      <div className="ck-toast-icon">{toastIcon}</div>
      <div className="ck-toast-body">
        <div className="ck-toast-heading">{heading}</div>
        {description && (
          <div className="ck-toast-description">{description}</div>
        )}
      </div>
      <button className="ck-toast-close" onClick={onClose}>
        <Close className="text-gray-400" width={14} height={14} />
      </button>
    </Alert>
  );
};
